import Manager from 'coreClasses/Manager';
import CookiesMgr from '../cookies/CookiesMgr';
import Cookie from 'coreClasses/cookies/Cookie';
import {isServer} from 'coreUtils/utils';
import {CMS_SCHEDULED_AT_COOKIE} from 'coreUtils/cms/contentHubConnection';
import type {ICpaConnection} from 'coreUtils/cms/contentHubConnection';

import {CMS_MODE_STORAGE_KEY} from 'brand/app/constants';

import {ECMSMode} from '../../store/data/configurations/types';

/**
 * Represents ContentMgr
 */
class ContentMgr extends Manager {
	private _ssrScheduledAt?: string;
	private _ssrCpaConnection?: ICpaConnection;
	/**
	 * Returns CMS Provider by Provider Id or current provider set in SitePreference
	 * @param {String|null} providerId Provider Id
	 * @returns {CMSProvider}
	 */
	getCMSProvider<T extends unknown = any>(providerId?: string): T {
		if (!providerId) {
			providerId = this.managerRepository.getConfigurationsMgr().get('sitePreferences.cmsProvider');
		}

		return this.managerRepository.getCmsProvidersMgr().getProvider(providerId) as T;
	}

	/**
	 * Checks whether we are in preview mode currently
	 * @returns {boolean} is in preview mode?
	 */
	isPreviewMode(): boolean {
		return this.managerRepository.getUiMgr().isCMSPreview();
	}

	/**
	 * Changes operation mode, live or preview
	 * @param {ECMSMode} mode - operation mode to be set
	 */
	changeMode(mode: ECMSMode) {
		!isServer &&
			CookiesMgr.setCookie(
				new Cookie(CMS_MODE_STORAGE_KEY, mode, {
					path: '/',
				}),
			);
	}

	/**
	 * Sets preview parameters for SSR context where browser cookies are unavailable.
	 * @param {string} [scheduledAt] - CPA `scheduled_at`
	 * @param {ICpaConnection} [cpaConnection] - CPA connection of the request (holds the token: server only)
	 */
	setSSRPreviewParams(scheduledAt?: string, cpaConnection?: ICpaConnection) {
		this._ssrScheduledAt = scheduledAt;
		this._ssrCpaConnection = cpaConnection;
	}

	/**
	 * Returns the CPA connection of the request on the server; `undefined` in the browser, where the CPA proxy
	 * resolves it.
	 */
	getCpaConnection(): ICpaConnection | undefined {
		return isServer ? this._ssrCpaConnection : undefined;
	}

	/**
	 * Returns the CPA scheduled_at value from SSR context or browser cookie.
	 */
	getScheduledAt(): string | undefined {
		if (isServer) {
			return this._ssrScheduledAt;
		}

		return CookiesMgr.getCookie(CMS_SCHEDULED_AT_COOKIE)?.getValue() || undefined;
	}

}

export default ContentMgr;
